/* eslint-disable react/no-unescaped-entities */
/* Bol Hub komponentleri — Ecommerce.js tarafından import edilir (named export). */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    MessageSquare, Star, Search,
    Loader2, RefreshCw, Sparkles,
    Check, AlertCircle, Send, FileSpreadsheet,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const INDICATOR_LABELS = {
    CANCELLATIONS: 'İptal Oranı',
    FULFILMENT: 'Sevkiyat Performansı',
    PHONE_AVAILABILITY: 'Telefon Erişilebilirliği',
    CASE_ITEM_RATIO: 'Şikayet Oranı',
    TRACK_AND_TRACE: 'Track & Trace',
    RETURNS: 'İade Oranı',
    REVIEWS: 'Müşteri Puanları',
};

/* ─── 5. INSIGHTS / PERFORMANCE KPI ────────────────────────────────────────── */

export function InsightsTab() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await axios.get(`${API}/api/bol/insights/performance`, { headers: hdrs() });
            setData(r.data);
        } catch (e) {
            toast.error(_err(e, 'Yüklenemedi'));
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    if (loading && !data) return <Spinner />;
    if (!data) return <EmptyHint label="Veri yok" />;

    const indicators = data.indicators || {};

    return (
        <div className="space-y-4" data-testid="insights-content">
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-gray-600">
                        {data.year} · Hafta {data.week}
                    </div>
                    <div className="text-sm text-gray-500">Bol.com satıcı performans göstergeleri</div>
                </div>
                <button onClick={load} disabled={loading}
                    className="h-9 px-3 text-sm font-semibold text-white bg-blue-700 hover:bg-blue-800 disabled:opacity-50 rounded-md flex items-center gap-1.5"
                    data-testid="insights-refresh">
                    {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                    Yenile
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {Object.entries(indicators).map(([key, ind]) => {
                    const passed = ind.passed === true;
                    const noData = ind.no_data;
                    return (
                        <div key={key} className={`p-4 rounded-lg border ${
                            noData ? 'bg-gray-50 border-gray-200'
                            : passed ? 'bg-emerald-50 border-emerald-200'
                            : 'bg-rose-50 border-rose-200'
                        }`}>
                            <div className="flex items-center justify-between mb-2">
                                <div className="text-xs font-bold uppercase tracking-wider text-gray-700">
                                    {INDICATOR_LABELS[key] || key}
                                </div>
                                {!noData && (passed ? (
                                    <Check size={14} className="text-emerald-600" />
                                ) : (
                                    <AlertCircle size={14} className="text-rose-600" />
                                ))}
                            </div>
                            {noData ? (
                                <div className="text-sm text-gray-400 italic">Bu hafta veri yok</div>
                            ) : (
                                <>
                                    <div className={`text-2xl font-black ${passed ? 'text-emerald-700' : 'text-rose-700'}`}>
                                        {ind.score !== null && ind.score !== undefined ? (typeof ind.score === 'number' ? ind.score.toFixed(2) : ind.score) : '—'}
                                    </div>
                                    <div className="text-[11px] text-gray-500 mt-1">
                                        Hedef: {ind.norm !== null && ind.norm !== undefined ? ind.norm : '—'}
                                    </div>
                                </>
                            )}
                        </div>
                    );
                })}
            </div>

            {(data.errors || []).length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded p-2 text-[11px] text-amber-800">
                    Hatalar: {data.errors.join(' · ')}
                </div>
            )}
        </div>
    );
}

/* ─── 6. KOMİSYON FATURALARI ──────────────────────────────────────────────── */

export function InvoicesTab() {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await axios.get(`${API}/api/bol/invoices`, { headers: hdrs() });
            setInvoices(r.data?.invoices || []);
        } catch (e) {
            toast.error(_err(e, 'Yüklenemedi'));
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const exportXlsx = async () => {
        setExporting(true);
        try {
            const r = await axios.get(`${API}/api/bol/invoices/export.xlsx`, {
                headers: hdrs(),
                responseType: 'blob',
            });
            const disp = r.headers['content-disposition'] || '';
            const m = disp.match(/filename="?([^"]+)"?/);
            const filename = m ? m[1] : `bol-faturalari-${Date.now()}.xlsx`;
            const url = window.URL.createObjectURL(new Blob([r.data]));
            const a = document.createElement('a');
            a.href = url; a.download = filename;
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Excel indirildi');
        } catch (e) {
            toast.error(_err(e, 'Excel indirilemedi'));
        } finally { setExporting(false); }
    };

    const downloadPdf = async (invId) => {
        try {
            const r = await axios.get(`${API}/api/bol/invoices/${invId}/pdf`, { headers: hdrs() });
            const b64 = r.data?.content_base64;
            if (!b64) return toast.error('PDF gelmedi');
            const blob = new Blob([Uint8Array.from(atob(b64), c => c.charCodeAt(0))], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
        } catch (e) { toast.error(_err(e, 'PDF hatası')); }
    };

    const markPushed = async (invId) => {
        try {
            await axios.post(`${API}/api/bol/invoices/${invId}/mark-billit-pushed`, {}, { headers: hdrs() });
            toast.success('Billit\'e gönderildi olarak işaretlendi');
            load();
        } catch (e) { toast.error(_err(e, 'Hata')); }
    };

    return (
        <div className="space-y-3" data-testid="invoices-content">
            <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-gray-900">Bol Komisyon/Fulfilment Faturaları</h3>
                <div className="flex gap-2">
                    <button onClick={load} disabled={loading}
                        className="h-9 px-3 text-sm font-semibold text-white bg-blue-700 hover:bg-blue-800 disabled:opacity-50 rounded-md flex items-center gap-1.5">
                        {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                        Çek
                    </button>
                    <button onClick={exportXlsx} disabled={exporting || loading || invoices.length === 0}
                        className="h-9 px-3 text-sm font-semibold text-white bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50 rounded-md flex items-center gap-1.5"
                        data-testid="bol-invoices-export-xlsx"
                        title="Fatura listesini Excel olarak indir">
                        {exporting ? <Loader2 size={14} className="animate-spin" /> : <FileSpreadsheet size={14} />}
                        Excel
                    </button>
                </div>
            </div>

            {loading && !invoices.length ? <Spinner /> : !invoices.length ? (
                <EmptyHint label="Bol'dan fatura çekilmedi" />
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead className="bg-gray-50 text-gray-600 uppercase">
                            <tr>
                                <th className="text-left p-2">Fatura No</th>
                                <th className="text-left p-2">Tarih</th>
                                <th className="text-right p-2">Komisyon</th>
                                <th className="text-right p-2">Kargo</th>
                                <th className="text-right p-2">Toplam</th>
                                <th className="text-left p-2">Billit</th>
                                <th className="text-right p-2">İşlem</th>
                            </tr>
                        </thead>
                        <tbody>
                            {invoices.map((inv) => {
                                const id = inv.invoiceId || inv.id || inv.invoice_id;
                                const total = inv.total_amount ?? inv.amount_total ?? inv.amount ?? inv.total ?? 0;
                                const date = inv.invoiceDate || inv.date || inv.invoice_date || '';
                                return (
                                    <tr key={id} className="border-b border-gray-100 hover:bg-gray-50">
                                        <td className="p-2 font-mono">{id}</td>
                                        <td className="p-2">{date ? date.slice(0, 10) : '—'}</td>
                                        <td className="p-2 text-right font-semibold text-rose-700">
                                            €{Number(inv.commission_amount || 0).toFixed(2)}
                                        </td>
                                        <td className="p-2 text-right font-semibold text-amber-700">
                                            €{Number(inv.transport_amount || 0).toFixed(2)}
                                        </td>
                                        <td className="p-2 text-right font-bold">
                                            €{Number(total).toFixed(2)}
                                        </td>
                                        <td className="p-2">
                                            {inv.billit_pushed
                                                ? <span className="text-emerald-700 font-bold">✓ Gönderildi</span>
                                                : <span className="text-gray-400">Bekliyor</span>
                                            }
                                        </td>
                                        <td className="p-2 text-right">
                                            <div className="flex justify-end gap-1">
                                                <button onClick={() => downloadPdf(id)}
                                                    className="h-7 px-2 text-[10px] font-semibold bg-gray-100 hover:bg-gray-200 rounded"
                                                    data-testid={`invoice-pdf-${id}`}>
                                                    PDF
                                                </button>
                                                {!inv.billit_pushed && (
                                                    <button onClick={() => markPushed(id)}
                                                        className="h-7 px-2 text-[10px] font-semibold text-white bg-blue-700 hover:bg-blue-800 rounded"
                                                        data-testid={`invoice-push-${id}`}>
                                                        Billit'e İşle
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

/* ─── 7. Q&A — MÜŞTERİ SORULARI ───────────────────────────────────────────── */

export function QnATab() {
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [state, setState] = useState('OPEN');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await axios.get(`${API}/api/bol/questions`, { headers: hdrs(), params: { state } });
            setQuestions(r.data?.questions || []);
        } catch (e) {
            toast.error(_err(e, 'Yüklenemedi'));
        } finally {
            setLoading(false);
        }
    }, [state]);
    useEffect(() => { load(); }, [load]);

    return (
        <div className="space-y-3" data-testid="qna-content">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <h3 className="text-sm font-bold text-gray-900">Müşteri Soruları</h3>
                    <select value={state} onChange={(e) => setState(e.target.value)}
                        className="h-8 px-2 text-xs border border-gray-300 rounded">
                        <option value="OPEN">Bekleyen</option>
                        <option value="ANSWERED">Cevaplanan</option>
                        <option value="ALL">Tümü</option>
                    </select>
                </div>
                <button onClick={load} disabled={loading}
                    className="h-9 px-3 text-sm font-semibold text-white bg-blue-700 hover:bg-blue-800 disabled:opacity-50 rounded-md flex items-center gap-1.5">
                    {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                    Yenile
                </button>
            </div>

            {loading && !questions.length ? <Spinner /> : !questions.length ? (
                <EmptyHint label={state === 'OPEN' ? 'Bekleyen soru yok 🎉' : 'Soru yok'} />
            ) : (
                <div className="space-y-2">
                    {questions.map((q) => (
                        <QuestionCard key={q.questionId || q.id} q={q} onAnswered={load} />
                    ))}
                </div>
            )}
        </div>
    );
}

function QuestionCard({ q, onAnswered }) {
    const qid = q.questionId || q.id;
    const [open, setOpen] = useState(false);
    const [answer, setAnswer] = useState('');
    const [aiBusy, setAiBusy] = useState(false);
    const [sending, setSending] = useState(false);
    const isAnswered = (q.state || q.status) === 'ANSWERED';

    const aiSuggest = async () => {
        setAiBusy(true);
        try {
            const r = await axios.post(`${API}/api/bol/questions/${qid}/ai-suggest`, {}, { headers: hdrs() });
            setAnswer(r.data?.suggested_answer || '');
            toast.success('AI önerisi hazır');
        } catch (e) {
            toast.error(_err(e, 'AI hatası'));
        } finally {
            setAiBusy(false);
        }
    };

    const send = async () => {
        if (!answer.trim()) return toast.error('Cevap boş');
        setSending(true);
        try {
            await axios.post(`${API}/api/bol/questions/${qid}/answer`, { answer }, { headers: hdrs() });
            toast.success('Cevap gönderildi');
            setOpen(false);
            onAnswered?.();
        } catch (e) {
            toast.error(_err(e, 'Gönderim hatası'));
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="border border-gray-200 rounded-lg bg-white" data-testid={`question-${qid}`}>
            <div className="p-3">
                <div className="flex items-start gap-2">
                    <MessageSquare size={14} className={`mt-0.5 ${isAnswered ? 'text-emerald-600' : 'text-amber-600'}`} />
                    <div className="flex-1 min-w-0">
                        <div className="text-sm text-gray-800">{q.subject || q.question || '—'}</div>
                        <div className="text-[11px] text-gray-500 mt-0.5">
                            EAN: {q.productEan || q.product?.ean || '—'}
                            {q.submissionDateTime && ` · ${q.submissionDateTime.slice(0, 16).replace('T', ' ')}`}
                            {q.expirationDateTime && (
                                <span className="ml-2 text-rose-600 font-semibold">
                                    ⏰ Bitiş: {q.expirationDateTime.slice(0, 10)}
                                </span>
                            )}
                        </div>
                    </div>
                    {!isAnswered && (
                        <button onClick={() => setOpen(!open)}
                            className="h-7 px-2 text-[10px] font-semibold text-white bg-blue-700 hover:bg-blue-800 rounded"
                            data-testid={`answer-btn-${qid}`}>
                            {open ? 'Kapat' : 'Cevapla'}
                        </button>
                    )}
                </div>
            </div>

            {open && (
                <div className="border-t border-gray-200 p-3 bg-gray-50 space-y-2">
                    <textarea value={answer} onChange={(e) => setAnswer(e.target.value)}
                        rows={4} maxLength={2000}
                        placeholder="Hollandaca cevabınızı yazın (max 2000 karakter)..."
                        className="w-full p-2 text-sm border border-gray-300 rounded-md font-sans"
                        data-testid={`answer-text-${qid}`} />
                    <div className="flex items-center gap-2">
                        <button onClick={aiSuggest} disabled={aiBusy}
                            className="h-9 px-3 text-xs font-semibold text-purple-700 bg-purple-50 hover:bg-purple-100 disabled:opacity-50 rounded-md flex items-center gap-1.5"
                            data-testid={`ai-suggest-${qid}`}>
                            {aiBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                            AI Cevap Öner
                        </button>
                        <button onClick={send} disabled={sending || !answer.trim()}
                            className="h-9 px-4 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 rounded-md flex items-center gap-1.5 ml-auto"
                            data-testid={`send-answer-${qid}`}>
                            {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                            Bol'a Gönder
                        </button>
                    </div>
                    <div className="text-[10px] text-gray-500">
                        {answer.length}/2000 karakter
                    </div>
                </div>
            )}
        </div>
    );
}

/* ─── 8. PRODUCT RATINGS ──────────────────────────────────────────────────── */

export function ReviewsTab() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [eanInput, setEanInput] = useState('');

    const loadCache = useCallback(async () => {
        try {
            const r = await axios.get(`${API}/api/bol/ratings/summary`, { headers: hdrs() });
            setItems(r.data?.items || []);
        } catch (e) { console.error(e); }
    }, []);
    useEffect(() => { loadCache(); }, [loadCache]);

    const fetchOne = async () => {
        const ean = eanInput.trim();
        if (!ean) return;
        setLoading(true);
        try {
            await axios.get(`${API}/api/bol/products/${ean}/ratings`, { headers: hdrs() });
            toast.success('Puanlar çekildi');
            setEanInput('');
            loadCache();
        } catch (e) {
            toast.error(_err(e, 'Hata'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-3" data-testid="reviews-content">
            <h3 className="text-sm font-bold text-gray-900">Ürün Puanları</h3>
            <div className="flex gap-2">
                <input value={eanInput} onChange={(e) => setEanInput(e.target.value)}
                    placeholder="EAN gir (Bol'dan puanları çek)..."
                    className="flex-1 h-9 px-3 text-sm border border-gray-300 rounded-md font-mono"
                    data-testid="ratings-ean-input" />
                <button onClick={fetchOne} disabled={loading || !eanInput.trim()}
                    className="h-9 px-4 text-sm font-semibold text-white bg-blue-700 hover:bg-blue-800 disabled:opacity-50 rounded-md flex items-center gap-1.5"
                    data-testid="ratings-fetch-btn">
                    {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                    Çek
                </button>
            </div>

            {!items.length ? <EmptyHint label="EAN girip puan çekin" /> : (
                <div className="space-y-2">
                    {items.map((it) => {
                        const avg = Number(it.average || 0);
                        const lowQuality = avg && avg < 4;
                        return (
                            <div key={it.ean} className={`p-3 border rounded-lg ${
                                lowQuality ? 'bg-rose-50 border-rose-200' : 'bg-white border-gray-200'
                            }`} data-testid={`rating-${it.ean}`}>
                                <div className="flex items-center justify-between flex-wrap gap-2">
                                    <span className="font-mono text-sm font-bold">{it.ean}</span>
                                    <div className="flex items-center gap-2">
                                        <div className="flex items-center gap-0.5">
                                            {[1, 2, 3, 4, 5].map(n => (
                                                <Star key={n} size={14}
                                                    className={n <= Math.round(avg) ? 'text-amber-500 fill-amber-500' : 'text-gray-300'} />
                                            ))}
                                        </div>
                                        <span className="text-sm font-bold">{avg.toFixed(1)}</span>
                                        <span className="text-[11px] text-gray-500">({it.total || 0} oy)</span>
                                    </div>
                                </div>
                                {lowQuality && (
                                    <div className="text-[11px] text-rose-700 mt-1 font-semibold">
                                        ⚠️ Düşük puan — ürün/operasyon kontrolü gerekiyor olabilir
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

/* ─── 10. CATALOG / BUY BOX ───────────────────────────────────────────────── */

export function CatalogTab() {
    const [ean, setEan] = useState('');
    const [product, setProduct] = useState(null);
    const [placement, setPlacement] = useState(null);
    const [loading, setLoading] = useState(false);

    const search = async () => {
        const v = ean.trim();
        if (!v) return;
        setLoading(true); setProduct(null); setPlacement(null);
        try {
            const [pRes, plRes] = await Promise.allSettled([
                axios.get(`${API}/api/bol/catalog/${v}`, { headers: hdrs() }),
                axios.get(`${API}/api/bol/catalog/${v}/placement`, { headers: hdrs() }),
            ]);
            if (pRes.status === 'fulfilled') setProduct(pRes.value.data);
            else toast.error(_err(pRes.reason, 'Ürün hatası'));
            if (plRes.status === 'fulfilled') setPlacement(plRes.value.data);
        } catch (e) { toast.error(_err(e, 'Hata')); }
        setLoading(false);
    };

    return (
        <div className="space-y-3" data-testid="catalog-content">
            <h3 className="text-sm font-bold text-gray-900">Katalog & Buy Box Analizi</h3>
            <div className="flex gap-2">
                <input value={ean} onChange={(e) => setEan(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && search()}
                    placeholder="EAN gir..."
                    className="flex-1 h-9 px-3 text-sm border border-gray-300 rounded-md font-mono"
                    data-testid="catalog-ean-input" />
                <button onClick={search} disabled={loading || !ean.trim()}
                    className="h-9 px-4 text-sm font-semibold text-white bg-blue-700 hover:bg-blue-800 disabled:opacity-50 rounded-md flex items-center gap-1.5"
                    data-testid="catalog-search-btn">
                    {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                    Ara
                </button>
            </div>

            {product && !product.not_found && (
                <div className="border border-gray-200 rounded-lg p-3 space-y-2">
                    <div className="font-bold text-sm">
                        {product.title || product.product?.title || '—'}
                    </div>
                    <div className="text-xs text-gray-600">
                        Brand: {product.brand || product.product?.brand || '—'}
                    </div>
                    {(product.attributes || product.product?.attributes || []).slice(0, 6).map((a, i) => (
                        <div key={i} className="text-[11px]">
                            <span className="text-gray-500">{a.name}:</span> <b>{Array.isArray(a.values) ? a.values.join(', ') : a.value}</b>
                        </div>
                    ))}
                </div>
            )}
            {product?.not_found && (
                <EmptyHint label="EAN Bol katalogunda bulunamadı" />
            )}

            {placement && !placement.no_placement_data && (
                <div className="border border-blue-200 bg-blue-50 rounded-lg p-3">
                    <div className="text-xs font-bold text-blue-900 mb-1">🎯 Buy Box / Placement</div>
                    <pre className="text-[10px] text-blue-900 whitespace-pre-wrap font-mono">
                        {JSON.stringify(placement, null, 2).slice(0, 1000)}
                    </pre>
                </div>
            )}
        </div>
    );
}

/* ─── 11. PROCESS STATUS ──────────────────────────────────────────────────── */

export function ProcessStatusTab() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = status ? { status } : {};
            const r = await axios.get(`${API}/api/bol/process-status`, { headers: hdrs(), params });
            setItems(r.data?.processStatuses || r.data?.processes || []);
        } catch (e) {
            toast.error(_err(e, 'Yüklenemedi'));
        } finally {
            setLoading(false);
        }
    }, [status]);
    useEffect(() => { load(); }, [load]);

    return (
        <div className="space-y-3" data-testid="process-content">
            <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-gray-900">Async İşlem Durumu</h3>
                <div className="flex items-center gap-2">
                    <select value={status} onChange={(e) => setStatus(e.target.value)}
                        className="h-8 px-2 text-xs border border-gray-300 rounded">
                        <option value="">Tümü</option>
                        <option value="PENDING">Bekliyor</option>
                        <option value="SUCCESS">Başarılı</option>
                        <option value="FAILURE">Başarısız</option>
                        <option value="TIMEOUT">Timeout</option>
                    </select>
                    <button onClick={load} disabled={loading}
                        className="h-9 px-3 text-sm font-semibold text-white bg-blue-700 hover:bg-blue-800 disabled:opacity-50 rounded-md flex items-center gap-1.5">
                        {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                        Yenile
                    </button>
                </div>
            </div>

            {loading && !items.length ? <Spinner /> : !items.length ? (
                <EmptyHint label="İşlem yok" />
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead className="bg-gray-50 text-gray-600 uppercase">
                            <tr>
                                <th className="text-left p-2">İşlem ID</th>
                                <th className="text-left p-2">Tip</th>
                                <th className="text-left p-2">Durum</th>
                                <th className="text-left p-2">Tarih</th>
                                <th className="text-left p-2">Mesaj</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((p, i) => {
                                const st = p.status || p.state;
                                const dotColor = st === 'SUCCESS' ? 'bg-emerald-500'
                                    : st === 'PENDING' ? 'bg-amber-500'
                                    : 'bg-rose-500';
                                return (
                                    <tr key={i} className="border-b border-gray-100">
                                        <td className="p-2 font-mono">{p.processStatusId || p.id}</td>
                                        <td className="p-2">{p.entityType || p.entityType || '—'}</td>
                                        <td className="p-2">
                                            <span className="inline-flex items-center gap-1">
                                                <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
                                                {st}
                                            </span>
                                        </td>
                                        <td className="p-2 text-gray-500">
                                            {(p.createTimestamp || p.timestamp || '').slice(0, 16).replace('T', ' ')}
                                        </td>
                                        <td className="p-2 text-gray-700">{(p.description || p.errorMessage || '').slice(0, 80)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

/* ─── HELPERS ─────────────────────────────────────────────────────────────── */

function Spinner() {
    return (
        <div className="text-center py-10">
            <Loader2 size={20} className="animate-spin mx-auto text-gray-400" />
        </div>
    );
}

function EmptyHint({ label }) {
    return (
        <div className="text-center py-10 text-sm text-gray-500 border-2 border-dashed border-gray-200 rounded-lg">
            {label}
        </div>
    );
}

function _err(e, fallback) {
    const d = e?.response?.data?.detail;
    if (typeof d === 'string') return d;
    if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join(', ');
    return d ? JSON.stringify(d).slice(0, 200) : fallback;
}
